<template>
  <div>
    <Header />

    <div class="title container-fluid text-center">
      <h1><strong>Privacy Policy</strong></h1>
    </div>

    <div class="content container">
      <p>
        This Privacy Policy explains how VIVA, a platform provided by Aim Solutions, Inc.
        (the “
        <strong>Company</strong>”, “
        <strong>we</strong>”, “
        <strong>us</strong>”, or “
        <strong>our</strong>”), doing business as “InitialView”, collects, uses, and protects
        personal information of users. We are committed to safeguarding your privacy and ensuring
        transparency about our data practices. By using our services, you agree to the data
        practices described in this policy.
      </p>

      <h3><strong>Information We Collect</strong></h3>
      <p>We may collect the following types of information:</p>
      <ul>
        <li>
          <p>
            <strong>Personal Information Provided by You:</strong>
            When you create an account or use our services, you may provide information such as name,
            email address, username, and any profile details. If the user is a child, a parent or
            guardian may provide this information on the child’s behalf.
          </p>
        </li>
        <li>
          <p>
            <strong>Usage Data:</strong>
            We automatically collect technical information when you interact with our services,
            including IP address, device type, browser type, and usage logs (e.g., pages viewed or
            features used). This helps us troubleshoot issues and improve our platform.
          </p>
        </li>
        <li>
          <p>
            <strong>Cookies and Similar Technologies:</strong>
            We use cookies and similar tracking technologies to remember your preferences and
            authenticate your sessions. Cookies also help us gather aggregate analytics about how
            users engage with our service, which we use to improve user experience. For example, we
            may use Google Analytics or similar tools to collect usage statistics. (See
            <strong>“Cookies & Tracking”</strong>
            below for more details.)
          </p>
        </li>
      </ul>

      <h3><strong>How We Use Your Information</strong></h3>
      <p>We use the collected information for purposes that include:</p>
      <ul>
        <li>
          <p>
            <strong>Providing and Improving the Service:</strong>
            Personal data is used to create accounts, enable login and authentication, deliver the
            features of our service, and personalize content. We also analyze usage data (via
            internal analysis or third-party analytics) to understand how our service is used and
            to make improvements.
          </p>
        </li>
        <li>
          <p>
            <strong>Support and Communication:</strong>
            We use contact information (like your email) to send service-related communications,
            respond to inquiries, provide customer support, and inform you of important updates or
            changes to the service.
          </p>
        </li>
        <li>
          <p>
            <strong>Research and Development:</strong>
            We may use data (in de-identified or aggregated form when required) to conduct
            internal research, develop new features, and enhance the effectiveness of our
            educational and engagement strategies. Any researchers with access to data will be
            authorized personnel bound by confidentiality agreements (see 
            <strong>“Data Access and Sharing”</strong>
            below).
          </p>
        </li>
        <li>
          <p>
            <strong>Legal and Security Purposes:</strong>
            Information may be used to enforce our Terms and Conditions (
            <strong>“Terms”</strong>
            ), prevent fraud or other prohibited activities, meet legal obligations, and ensure the
            security and integrity of our systems. For example, IP addresses and logs may be used
            to detect misuse or unauthorized access.
          </p>
        </li>
      </ul>

      <h3><strong>Data Storage and Security</strong></h3>
      <p>
        Your data is stored on secure servers provided by Google Cloud, typically located in the
        United States. We take reasonable measures to protect personal information from
        unauthorized access or disclosure. This includes using encryption for data in transit
        (e.g., SSL/TLS for our website) and at rest whenever applicable, strong authentication
        measures, and regular security reviews. Access to personal data is restricted to authorized
        personnel who need it to perform their job duties, such as support staff, software
        engineers, and researchers. All employees and independent contractors with such access are
        bound by confidentiality agreements. They may only access and use user data for purposes
        relevant to their role in operating or improving the service. We also maintain administrative
        and technical safeguards to protect data (e.g., firewalls, access logs, and periodic security
        audits). However, please note that no method of transmission over the internet or electronic
        storage is 100% secure, so while we strive to protect your data, we cannot guarantee absolute
        security.
      </p>

      <h3><strong>Data Sharing and Disclosure</strong></h3>
      <p>
        We do
        <strong>not</strong>
        sell or rent your personal information to third parties. We only share information in the 
        following circumstances:
      </p>
      <ul>
        <li>
          <p>
            <strong>Internal and Service Providers:</strong>
            As noted, your data may be accessed by our authorized team members (including support
            staff, engineers, and researchers) and trusted independent contractors who assist us in
            operating and improving the service. All such persons are obligated to keep information
            confidential and use it only for their work duties.
          </p>
        </li>
        <li>
          <p>
            <strong>Cloud Storage and Infrastructure:</strong>
            Your data is stored on Google Cloud in the U.S., and Google acts as a data host on our
            behalf.
          </p>
        </li>
        <li>
          <p>
            <strong>Analytics Partners:</strong>
            We use third-party analytics services (for example, Google Analytics) to collect and
            analyze usage information. These third parties may receive certain pseudonymous data
            (like cookie identifiers and usage events) in order to provide aggregate insights to
            us. Please refer to their privacy policies for details on how they handle information.
          </p>
        </li>
        <li>
          <p>
            <strong>Legal Requirements:</strong>
            We may disclose information if required to comply with a law, regulation, legal
            process, or governmental request (for example, responding to a subpoena or court
            order). If we receive a verified request from a law enforcement agency, or if
            disclosure is necessary to investigate or remedy illegal activities, fraud, or security
            vulnerabilities, we may share the requested information as required by law.
          </p>
        </li>
        <li>
          <p>
            <strong>Business Transfers:</strong>
            If we are involved in a merger, acquisition, bankruptcy, or sale of all or a portion of
            our assets, user information may be transferred to the successor or partner entity as
            part of that transaction.
          </p>
        </li>
        <li>
          <p>
            <strong>With Consent:</strong>
            Apart from the cases above, we will share personal information with third parties only
            if you (or your parent/guardian, if applicable) have given us explicit consent to do so.
          </p>
        </li>
      </ul>

      <h3><strong>Cookies & Tracking Technologies</strong></h3>
      <p>
        We use cookies (small text files stored on your device) and similar technologies to enhance
        your experience and gather usage data:
      </p>
      <ul>
        <li>
          <p>
            <strong>Operational Cookies:</strong>
            Some cookies are necessary for the site to function – for example, to keep you logged
            in or remember your preferences. These are enabled by default to provide a smooth user
            experience.
          </p>
        </li>
        <li>
          <p>
            <strong>Preference Cookies:</strong>
            We may use cookies to remember choices you make (like language or font size) to
            personalize your experience.
          </p>
        </li>
        <li>
          <p>
            <strong>Analytics Cookies:</strong>
            We use third-party analytics cookies (such as Google Analytics cookies) to collect
            information about how users interact with our service. This helps us analyze traffic
            patterns and user behavior so we can improve content and functionality. The information
            collected typically includes data like your device type, browser, pages visited, time
            spent, and approximate geolocation. It does
            <strong>not</strong>
            directly identify you by name.
          </p>
        </li>
        <li>
          <p>
            <strong>Your Choices for Cookies:</strong>
            Most web browsers automatically accept cookies, but you can usually modify your browser
            setting to decline cookies or alert you when a cookie is being placed. Please note that
            if you disable cookies entirely, some features of our service (like login sessions) may
            not function properly. By continuing to use our service without disabling cookies, you
            consent to our use of cookies as described here.
          </p>
        </li>
      </ul>

      <h3><strong>Children’s Privacy and Parental Access</strong></h3>
      <p>
        Protecting children’s privacy is very important to us. Our service may be used by children
        (for example, students) under the age of 13, but
        <strong>only with the consent and supervision of a parent or legal guardian</strong>.
        We comply with the U.S. Children’s Online Privacy Protection Act (COPPA) requirements for
        the protection of children’s data. Key practices include:
      </p>
      <ul>
        <li>
          <p>
            <strong>Parental Consent:</strong>
            If a user is under 13, a parent or guardian must provide consent for the child to use
            our service and for us to collect the child’s personal information. We may require the
            parent to register the account on behalf of the child or otherwise verify consent in
            compliance with COPPA guidelines. We do not knowingly allow a child under 13 to sign
            up for our services without parental involvement.
          </p>
        </li>
        <li>
          <p>
            <strong>Parental Access to Data:</strong>
            Parents and guardians have the right to review personal information collected from
            their children and to request deletion or cessation of further collection. To facilitate this,
            <strong>we allow parents to use their child’s login credentials to access and review
            the child’s account and data</strong>.
            In other words, if you are a parent or guardian,you can log in using your child’s
            account (with the username/email and password) to see the information associated with
            that account and make updates or deletions to the profile as needed. We do not require
            separate parent accounts or verification in order for a parent to access their child’s
            data; we assume that possession of the login credentials implies authorized
            parent/guardian access.
            <strong>Please keep your child’s login information confidential and secure</strong>,
            and supervise its use.
          </p>
        </li>
        <li>
          <p>
            <strong>Limited Use of Child Data:</strong>
            We use children’s personal information only to provide and improve the service (for
            example, to personalize learning content or track progress), and as otherwise permitted
            by law. We do not share a child’s personal information with third parties except as
            necessary for the operation of the service (such as our cloud storage and analytics
            providers as described above, which are all obligated to maintain the confidentiality
            and security of the data). We will not use children’s data for marketing or advertising
            purposes directed at the child.
          </p>
        </li>
        <li>
          <p>
            <strong>Parental Controls:</strong>
            If at any time a parent or guardian wants to revoke consent, review their child’s
            personal information, or request that we delete the child’s information, they can do so
            by contacting us. We will verify the request is from the parent/guardian and comply
            with applicable law in fulfilling such requests. Keep in mind that removing certain
            data or accounts may affect the child’s ability to use the service.
          </p>
        </li>
      </ul>

      <h3><strong>Your Rights and Choices</strong></h3>
      <p>
        We strive to give all users (and parents of child users) appropriate control over personal
        information:
      </p>
      <ul>
        <li>
          <p>
            <strong>Access and Correction:</strong>
            You can access and update certain profile information by logging into your account
            settings. If any personal information we have is inaccurate, you can correct it through
            your account or by contacting us for assistance.
          </p>
        </li>
        <li>
          <p>
            <strong>Data Deletion:</strong>
            You may request deletion of your account or personal data by contacting our support
            from your email address that we have on file. We will review such requests and delete
            information that we are not required or permitted to retain by law or for legitimate
            business purposes. Note that residual copies of your data may persist in backup storage
            for a brief period, but we will ensure it is not accessible or used in our active
            systems after deletion.
          </p>
        </li>
        <li>
          <p>
            <strong>Opt-Out of Communications:</strong>
            If you receive promotional or newsletter emails from us, you can opt out at any time by
            clicking the “unsubscribe” link in those emails or adjusting your account email
            preferences. We will still send you essential service or transactional communications
            (such as password resets or important account notices) when necessary.
          </p>
        </li>
        <li>
          <p>
            <strong>Cookies:</strong>
            As described above, you can control or disable cookies through your browser settings.
            Our site does not currently respond to “Do Not Track” signals, but you can opt out of
            certain analytics tracking by using tools like the Google Analytics opt-out browser
            add-on.
          </p>
        </li>
        <li>
          <p>
            <strong>California Privacy Rights:</strong>
            While our service is initially directed at U.S. users generally, and we may not meet
            the applicability thresholds of the California Consumer Privacy Act (CCPA) at this
            time, we still value transparency. We do not sell personal information. California
            residents may have the right to request details about the categories of personal
            information we collect, the sources of that information, the purposes for collecting
            it, and the categories of third parties with whom we share it. You may also have the
            right to request deletion of your personal information and the right not to be
            discriminated against for exercising these rights. If you are a California resident and
            would like to exercise any applicable rights, please contact us and we will respond in
            accordance with applicable law. (Verification of your identity may be required.)
          </p>
        </li>
      </ul>

      <h3><strong>Data Retention</strong></h3>
      <p>
        We endeavor to keep personal information only for as long as necessary to fulfill our
        business obligations, fulfill the purposes described in this policy or as required by law.
        For example, we retain account information while your account is active. We periodically
        review the data we hold and either delete or anonymize information that is no longer
        needed. If deletion is not immediately possible (for instance, if the data is stored in
        backups), we endeavor to ensure it is securely isolated and protected until deletion is
        feasible.
      </p>

      <h3><strong>Changes to This Privacy Policy</strong></h3>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our practices,
        technologies, legal requirements, or other factors. We encourage you to review this policy
        periodically for any updates. Continued use of the service after any changes signifies your
        acceptance of the revised policy.
      </p>

      <h3><strong>Contact Us</strong></h3>
      <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data
        practices, please contact us at
        <a href="mailto:contact@initialview.com">contact@initialview.com</a>.
      </p>
      <p>
        We will respond to your inquiries and address your concerns as promptly as possible. Your
        privacy is important to us, and we welcome your feedback.
      </p>
    </div>
  </div>
</template>

<script>
import Header from '@/components/Header.vue'

export default {
  name: 'PrivacyPolicy',
  components: {
    Header
  }
}
</script>

<style scoped>
p {
  color: #333;
}

.title {
  background-color: var(--viva-green);
}

.title h1 {
  margin-top: 3rem;
  margin-bottom: 3rem;
  color: var(--viva-cream);
}

.content::before {
  margin-top: 3rem;
}

.content::after {
  margin-bottom: 6rem;
}
</style>